import {
  createContext,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export const ModalPortalContext = createContext<HTMLElement | null>(null);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Accessible name for a modal without a `title` (the title names the dialog when present). */
  ariaLabel?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';
  children: ReactNode;
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-[calc(100vw-2rem)] sm:max-w-sm',
  md: 'max-w-[calc(100vw-2rem)] sm:max-w-md',
  lg: 'max-w-[calc(100vw-2rem)] sm:max-w-lg',
  xl: 'max-w-[calc(100vw-2rem)] sm:max-w-xl',
  '2xl': 'max-w-[calc(100vw-2rem)] sm:max-w-2xl',
  '4xl': 'max-w-[calc(100vw-2rem)] sm:max-w-2xl md:max-w-4xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  size = 'md',
  children,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  // Element where the most recent mousedown started — see handleBackdropClick.
  const mouseDownTargetRef = useRef<EventTarget | null>(null);
  const titleId = useId();

  const mergedRef = useCallback((el: HTMLDialogElement | null) => {
    dialogRef.current = el;
    setPortalContainer(el);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      openerRef.current = document.activeElement as HTMLElement | null;
      dialog.showModal();
    } else {
      dialog.close();
      openerRef.current?.focus?.();
      openerRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  // Close only when BOTH the mousedown and the click landed on the <dialog>
  // backdrop itself. Guarding on the mousedown origin (not just the click
  // target) prevents a "twitch then close": picking an option in a portaled
  // dropdown (e.g. SearchableSelect) removes the dropdown on mousedown, so the
  // trailing click resolves on the backdrop and would otherwise close the modal.
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === dialogRef.current && mouseDownTargetRef.current === dialogRef.current) {
      onClose();
    }
  };

  return (
    <ModalPortalContext.Provider value={portalContainer}>
      <dialog
        ref={mergedRef}
        onMouseDown={handleMouseDown}
        onClick={handleBackdropClick}
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        aria-modal="true"
        className={`m-auto w-full overflow-visible bg-transparent p-0 backdrop:bg-black/50 ${sizeClasses[size]}`}
      >
        {/* Panel is a height-capped flex column: header stays put, only the body
            scrolls. overflow-hidden + rounded-2xl clips the body's scrollbar to
            the rounded panel (no more detached scrollbar on the bare <dialog>).
            In-modal dropdowns/date-pickers are unaffected — Popover + SearchableSelect
            portal out of this overflow via ModalPortalContext / position:fixed. */}
        <div
          className={`animate-slide-up flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-800 ${className ?? ''}`}
        >
          {title && (
            <div className="flex shrink-0 items-center justify-between p-6 pb-0">
              <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h2>
              <button
                onClick={onClose}
                aria-label="Close modal"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
          <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {children}
          </div>
        </div>
      </dialog>
    </ModalPortalContext.Provider>
  );
}
